import type { FieldValues } from 'react-hook-form';
import isPlainObject from 'lodash.isplainobject';

import { ArrayFields, ConsignmentTabFields, HeaderTabFields, NamesAndPartiesTabFields } from '../types/fieldEnums';
import { type IAdditionalField, type IDBOptionAdditionalFieldRequest, type ILpcoRequest } from '../types/request';
import { type IFeeDto, type IPackage, type ITreatment } from '../types/response';
import { FieldPrefix } from '../constants/fieldPrefixes';
import { OperationType } from '../types/enums';

import { filterObjectByFields } from '../../../helpers/filterObjectByFields';
import { formatDateForBackend } from '../../../helpers/dateHelpers';
import { type IFeeGroup } from './groupByBillNumber';

type IAdditionalFieldsFormValues = Record<string, unknown>;

export function transformAdditionalFields(data: FieldValues, editableFields?: string[]): IAdditionalField[] {
  const transformedData: Record<string, IAdditionalField> = {};

  Object.entries(data).forEach(([key, value]) => {
    const fieldId = Number(key.replace(FieldPrefix.additionalField, ''));

    if (isPlainObject(value)) {
      transformedData[key] = { fieldId, fieldValue: value?.value ?? null };
    } else if (value instanceof Date) {
      transformedData[key] = { fieldId, fieldValue: formatDateForBackend(value) };
    } else {
      transformedData[key] = { fieldId, fieldValue: value };
    }
  });

  if (editableFields) {
    const filteredEditableFields: Record<string, IAdditionalField> = filterObjectByFields(
      transformedData,
      editableFields,
      FieldPrefix.additionalFields
    );

    return Object.values(filteredEditableFields);
  }

  return Object.values(transformedData);
}

export function transformDBOptionAdditionalFields(
  data: FieldValues,
  editableFields?: string[]
): IDBOptionAdditionalFieldRequest[] {
  const transformedData: Record<string, IDBOptionAdditionalFieldRequest> = {};

  Object.entries(data).forEach(([key, value]) => {
    const fieldId = Number(key.replace(FieldPrefix.additionalField, ''));

    transformedData[key] = { fieldId, optionId: value?.value ?? null };
  });

  if (editableFields) {
    const filteredEditableFields: Record<string, IDBOptionAdditionalFieldRequest> = filterObjectByFields(
      transformedData,
      editableFields,
      FieldPrefix.additionalFields
    );

    return Object.values(filteredEditableFields);
  }

  return Object.values(transformedData);
}

export function convertFieldDataForBackEnd(
  data: FieldValues,
  isPredefinedOperation: boolean,
  editableFields: (OperationType | string)[]
) {
  const { selectedOperation: operation, ...restData } = data;
  const { lpcoFields, itemFields, feeFields, attachedDocumentFields, packageFields, treatmentFields } =
    divideEditableFields(editableFields);

  const {
    items,
    fees: groupedFees,
    beneficiaries,
    attachedDocuments,
    additionalFields,
    dbOptionAdditionalFields,
    ...convertedData
  } = {
    ...restData,
    [HeaderTabFields.licenseType]: restData.licenseType?.value || null,
    [HeaderTabFields.extendTo]: formatDateForBackend(restData.extendTo as Date | null),
    [HeaderTabFields.countryOfDestinationCode]: restData.countryOfDestinationCode?.value || null,
    [HeaderTabFields.departmentOfficeCode]: restData.departmentOfficeCode?.value || null,
    [HeaderTabFields.customsOfficeCode]: restData.customsOfficeCode?.value || null,
    [HeaderTabFields.typeOfUse]: restData.typeOfUse?.value || null,
    [HeaderTabFields.termsOfDeliveryCode]: restData.termsOfDeliveryCode?.value || null,
    [HeaderTabFields.invoiceCurrencyCode]: restData.invoiceCurrencyCode?.value || null,
    [HeaderTabFields.placeOfLoadingCode]: restData.placeOfLoadingCode?.value || null,
    [HeaderTabFields.placeOfUnloadingCode]: restData.placeOfUnloadingCode?.value || null,
    [HeaderTabFields.validTo]: formatDateForBackend(restData.validTo as Date | null),
    [NamesAndPartiesTabFields.companyCode]: restData.companyCode?.value || null,
    [ConsignmentTabFields.transitCountryCode]: restData.transitCountryCode?.value || null,
    [ConsignmentTabFields.meanOfTransportCode]: restData.meanOfTransportCode?.value || null,
  } as ILpcoRequest & { fees?: IFeeGroup[] };

  let lpcoAdditionalFields;
  let lpcoDbOptionAdditionalFields;

  if (additionalFields) {
    lpcoAdditionalFields = transformAdditionalFields(additionalFields, lpcoFields);
  }

  if (dbOptionAdditionalFields) {
    lpcoDbOptionAdditionalFields = transformDBOptionAdditionalFields(dbOptionAdditionalFields, lpcoFields);
  }

  const finalData: ILpcoRequest = filterObjectByFields(convertedData, lpcoFields);
  finalData.additionalFields = lpcoAdditionalFields ?? [];
  finalData.dbOptionAdditionalFields = lpcoDbOptionAdditionalFields ?? [];

  if (attachedDocuments?.length) {
    finalData[ArrayFields.attachedDocuments] = attachedDocumentConverter(attachedDocuments, attachedDocumentFields);
  }

  if (items?.length) {
    finalData[ArrayFields.items] = itemsConverter({
      items,
      editableFields: itemFields,
      packageEditableFields: packageFields,
      treatmentEditableFields: treatmentFields,
    });
  }

  if (groupedFees?.length) {
    const feeValues = (groupedFees as IFeeGroup[]).reduce(
      (acc, { receiptNumber, paymentDate, fees }) => [
        ...acc,
        ...fees.map((fee) => ({
          ...fee,
          receiptNumber,
          paymentDate: formatDateForBackend(paymentDate),
        })),
      ],
      [] as IFeeDto[]
    );
    finalData[ArrayFields.fees] = feeValues.map((fee) => filterObjectByFields(fee, feeFields));
  }

  if (!isPredefinedOperation || operation === OperationType.APPROVE || operation === OperationType.APPROVE_AMENDMENT) {
    finalData.ogaOperation = operation;
  }

  return finalData;
}

function attachedDocumentConverter(attachments: FieldValues[], editableFields: string[]) {
  return attachments.map(({ parentAttachmentId, ...attachment }) =>
    filterObjectByFields(
      {
        ...attachment,
        code: attachment.code?.value || null,
        attachmentDate: formatDateForBackend(attachment.attachmentDate as Date),
      },
      editableFields
    )
  );
}

export function itemConverter({
  item: { additionalFields, dbOptionAdditionalFields, packages, treatments, ...item },
  editableFields,
  packageEditableFields,
  treatmentEditableFields,
}: {
  item: FieldValues;
  editableFields: string[];
  packageEditableFields: string[];
  treatmentEditableFields: string[];
}) {
  let itemAdditionalFields;
  let itemDbOptionAdditionalFields;

  if (additionalFields) {
    itemAdditionalFields = transformAdditionalFields(additionalFields as IAdditionalFieldsFormValues, editableFields);
  }

  if (dbOptionAdditionalFields) {
    itemDbOptionAdditionalFields = transformDBOptionAdditionalFields(
      dbOptionAdditionalFields as IAdditionalFieldsFormValues,
      editableFields
    );
  }

  const finalData = filterObjectByFields(
    {
      ...item,
      commodityCode: item.commodityCode?.value || null,
      countryOfOriginCode: item.countryOfOriginCode?.value || null,
      countryOfOriginName: item.countryOfOriginCode?.tag?.label || null,
      packageTypeCode: item.packageTypeCode?.value || null,
      packageTypeName: item.packageTypeCode?.tag?.label || null,
      requestedUomCode: item.requestedUomCode?.value || null,
      requestedUomName: item.requestedUomCode?.label?.description || null,
      approvedUomCode:
        item.approvedAmount || item.approvedUomCode?.value !== item.requestedUomCode?.value
          ? item.approvedUomCode?.value || null
          : null,
      approvedUomName: item.requestedUomName?.label?.description || null,
    },
    editableFields
  );

  finalData.additionalFields = itemAdditionalFields ?? [];
  finalData.dbOptionAdditionalFields = itemDbOptionAdditionalFields ?? [];
  finalData.packages = packages.map((packageData: IPackage[]) =>
    filterObjectByFields(packageData, packageEditableFields)
  );
  finalData.treatments = treatments.map((treatment: ITreatment[]) =>
    filterObjectByFields(treatment, treatmentEditableFields)
  );

  return finalData;
}

function itemsConverter({
  items,
  editableFields,
  packageEditableFields,
  treatmentEditableFields,
}: {
  items: FieldValues[];
  editableFields: string[];
  packageEditableFields: string[];
  treatmentEditableFields: string[];
}) {
  return items.map((item) => itemConverter({ item, editableFields, packageEditableFields, treatmentEditableFields }));
}

export function divideEditableFields(editableFields: string[]) {
  const lpcoFields: string[] = ['id'];
  const attachedDocumentFields: string[] = ['id'];
  const itemFields: string[] = ['id'];
  const feeFields: string[] = ['id'];
  const beneficiaryFields: string[] = ['id'];
  const packageFields: string[] = ['id'];
  const treatmentFields: string[] = ['id'];

  editableFields.forEach((item) => {
    if (item.startsWith(FieldPrefix.attachedDocument)) {
      attachedDocumentFields.push(item.replace(FieldPrefix.attachedDocument, ''));
    } else if (item.startsWith(FieldPrefix.packages)) {
      packageFields.push(item.replace(FieldPrefix.packages, ''));
    } else if (item.startsWith(FieldPrefix.treatments)) {
      treatmentFields.push(item.replace(FieldPrefix.treatments, ''));
    } else if (item.startsWith(FieldPrefix.item)) {
      itemFields.push(item.replace(FieldPrefix.item, ''));
    } else if (item.startsWith(FieldPrefix.fee)) {
      feeFields.push(item.replace(FieldPrefix.fee, ''));
    } else if (item.startsWith(FieldPrefix.beneficiary)) {
      beneficiaryFields.push(item.replace(FieldPrefix.beneficiary, ''));
    } else if (item.startsWith(FieldPrefix.lpco)) {
      lpcoFields.push(item.replace(FieldPrefix.lpco, ''));
    }
  });

  return {
    lpcoFields,
    itemFields,
    attachedDocumentFields,
    feeFields,
    beneficiaryFields,
    packageFields,
    treatmentFields,
  };
}
